import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';

import {
  type BtsHeroFormalDegreeKey,
  type BtsHeroHeadline,
  type BtsHeroVariant,
  BTS_HERO_CTA_LABEL,
  BTS_HERO_DESKTOP_ART_WIDTH,
  BTS_HERO_PAGE_CONTENT,
  BTS_HERO_STRIKETHROUGH_IMAGE,
  getBtsHeroCheckoutHref,
} from '../constants/bts-hero-content';

export function BtsHeroTakeoverSection({
  formalDegreeKey,
  variant,
  downloadBrochureButtonSlot = null,
}: {
  formalDegreeKey: BtsHeroFormalDegreeKey;
  variant: BtsHeroVariant;
  downloadBrochureButtonSlot?: ReactNode;
}) {
  const config = BTS_HERO_PAGE_CONTENT[formalDegreeKey];
  const headline: BtsHeroHeadline = config.headlines[variant];

  // Mirrors AccreditationHeroSection: subscribers add the degree application to their subscription,
  // everyone else buys the standalone degree application. CLS-safe: SSR renders the non-subscriber
  // href, then the client corrects it for subscribers (same flash as the default hero).
  const clientReady = useClientRender();
  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;
  const checkoutHref = getBtsHeroCheckoutHref({ formalDegreeKey, hasActiveSubscription, variant });

  return (
    <HeroLayout
      backgroundImageOverlayGradient={null}
      bgColor={config.backgroundColor}
      bgGradient={config.viewportBackgroundGradient}
      textAndCtaContainerProps={{ gap: { base: '24px', md: '40px' } }}
      backgroundImageSlot={
        <>
          <Box display={{ base: 'block', md: 'none' }} inset={0} maxWidth='369px' position='absolute'>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src={config.backgroundImages.mobile}
              style={{ objectFit: 'contain', objectPosition: 'left top' }}
              fill
              priority
            />
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            position='absolute'
            right={0}
            top={0}
            width={BTS_HERO_DESKTOP_ART_WIDTH}
          >
            <Image
              alt=''
              quality={100}
              sizes='(max-width: 1440px) 57vw, 821px'
              src={config.backgroundImages.desktop}
              style={{ objectFit: 'contain', objectPosition: 'right center' }}
              fill
              priority
            />
          </Box>
        </>
      }
      ctaSlot={
        <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }}>
          <ButtonLink
            linkProps={{ href: checkoutHref }}
            buttonProps={{
              leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
              size: 'icon-left',
              variant: 'solid',
              colorScheme: 'blue',
              width: { base: '100%', md: 'fit-content' },
              onClick: () => {
                trackEvent({
                  type: 'click',
                  name: 'Masters Page CTA Clicked',
                  label: 'hero_enroll_now',
                  additionalAttributes: {
                    formal_degree_key: formalDegreeKey,
                    bts_hero_variant: variant,
                  },
                });
              },
            }}
          >
            {BTS_HERO_CTA_LABEL}
          </ButtonLink>
          {downloadBrochureButtonSlot}
        </Flex>
      }
      textContentSlot={
        <Flex
          flexDirection='column'
          gap={{ base: '16px', md: '24px' }}
          maxWidth={{ base: '100%', md: '500px', lg: '600px', xl: '800px' }}
          pt={{ base: '180px', md: '0' }}
          whiteSpace='pre-wrap'
        >
          <Heading
            as='h1'
            color='white'
            fontSize={{ base: '38px', md: '46px', lg: '56px', xl: '76px' }}
            fontWeight={{ base: '600', md: '500' }}
            letterSpacing={{ base: '-0.76px', md: '-0.92px', lg: '-1.12px', xl: '-1.52px' }}
            lineHeight={{ base: '1.1', md: '1.05', xl: '1' }}
          >
            <Box as='span' color='#EDA93B' display='block'>
              {headline.eyebrow}
            </Box>
            {headline.prefix}
            <Box as='span' display={headline.priceOnOwnLine ? 'block' : 'inline'}>
              {headline.priceLabel}
              {headline.originalPrice ? (
                <Box as='span' color='#9C9FA8' position='relative'>
                  {headline.originalPrice}
                  <Box
                    h={{ base: '6px', md: '12px' }}
                    left='0'
                    pointerEvents='none'
                    position='absolute'
                    right='0'
                    top={{ base: '50%', md: '55%' }}
                    transform='translateY(-50%)'
                    w='auto'
                    aria-hidden
                  >
                    <Image
                      alt=''
                      height={75}
                      src={BTS_HERO_STRIKETHROUGH_IMAGE}
                      style={{ objectFit: 'contain', objectPosition: 'left center' }}
                      width={400}
                    />
                  </Box>
                </Box>
              ) : null}
              {headline.salePrice}
            </Box>
          </Heading>
          <Text color={{ base: '#9C9FA8', md: '#CECFD4' }} fontSize={{ base: '16px', md: '18px' }} lineHeight='1.5'>
            {config.subtitle}
          </Text>
        </Flex>
      }
    />
  );
}
